import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { Outlet } from "react-router-dom";
import RewampBreadCrumbsBar from "./RewampBreadCrumbsBar";
import RewampPagination from "./RewampPagination";
import { RewampPageProvider } from "./RewampPageProvider";
import { shellSxFromSpec, shellBodySxFromSpec } from "../sections/sectionLayout";

export const REWAMP_PAGE_SHELL_SX = {
  width: "100%",
  px: (theme) => theme.palette.layout.pagePadding,
  pt: (theme) => theme.palette.layout.pagePaddingTop,
  pb: (theme) => theme.palette.layout.pagePaddingBottom,
};

const DEFAULT_SECTION_GAP = (theme) => theme.palette.layout.sectionGap;

function ShellFrame({ breadcrumb = false, pagination, sectionGap, outletContext, sx, bodySx, children }) {
  const body = children ?? <Outlet context={outletContext} />;
  return (
    <Box
      sx={{
        ...REWAMP_PAGE_SHELL_SX,
        display: "flex",
        flexDirection: "column",
        gap: sectionGap ?? DEFAULT_SECTION_GAP,
        ...sx,
      }}
    >
      {breadcrumb ? (
        <RewampBreadCrumbsBar {...(breadcrumb === true ? {} : breadcrumb)} />
      ) : null}
      {bodySx ? <Box sx={bodySx}>{body}</Box> : body}
      {pagination ? <RewampPagination {...pagination} /> : null}
    </Box>
  );
}

// With `spec`, the shell owns pagination state (spec.shell.pagination) and page context; the
// `pagination` prop is the pre-spec escape hatch and is ignored when a spec is given.
export default function RewampPageShell({ spec, pageData, pageContext, breadcrumb, pagination, sectionGap, outletContext, sx, children }) {
  if (!spec) {
    return (
      <ShellFrame breadcrumb={breadcrumb} pagination={pagination} sectionGap={sectionGap} outletContext={outletContext} sx={sx}>
        {children}
      </ShellFrame>
    );
  }
  const shell = spec.shell ?? {};
  return (
    <RewampPageProvider spec={spec} pageData={pageData} pagination={pageContext}>
      {(ctx) => (
        <ShellFrame
          breadcrumb={breadcrumb ?? shell.breadcrumb ?? false}
          pagination={ctx.pagination.shellProps}
          sectionGap={sectionGap ?? shell.sectionGap}
          outletContext={ctx}
          sx={{ ...shellSxFromSpec(shell), ...sx }}
          bodySx={shellBodySxFromSpec(shell)}
        >
          {typeof children === "function" ? children(ctx) : children}
        </ShellFrame>
      )}
    </RewampPageProvider>
  );
}

RewampPageShell.propTypes = {
  /** Page spec; `shell.{geometry,sectionGap,breadcrumb,pagination}` drive the frame and page context. */
  spec: PropTypes.shape({ shell: PropTypes.object }),
  /** Module data merged into the page context (e.g. the schemes MDMS rows). */
  pageData: PropTypes.object,
  /** A usePagePagination() instance the page already owns (data hook needs page/pageSize first). */
  pageContext: PropTypes.object,
  /** false/omit = no breadcrumb; true = bare <RewampBreadCrumbsBar/>; object = its props. */
  breadcrumb: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  /** Falsy = no pagination row; object = spread onto <RewampPagination/>. */
  pagination: PropTypes.object,
  sectionGap: PropTypes.oneOfType([PropTypes.number, PropTypes.func, PropTypes.object]),
  /** Passed to the shell's own <Outlet/> — ignored when `children` is given. */
  outletContext: PropTypes.object,
  sx: PropTypes.object,
  /** Node, or with `spec` a render function receiving the page context. */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

RewampPageShell.defaultProps = {
  spec: null,
  pageData: undefined,
  pageContext: null,
  breadcrumb: undefined,
  pagination: null,
  sectionGap: undefined,
  outletContext: undefined,
  sx: null,
  children: undefined,
};
